import { BpmDateString } from "@blisspointmedia/bpm-types/dist/FiscalCalendar";
import {
  Forecast,
  ForecastGroup,
  OutcomeVariableType
} from "@blisspointmedia/bpm-types/dist/Forecasting";
import { abbreviateNumber, formatMoney, formatMoneyAsInt } from "../utils/format-utils";

export const ECON_BUCKET = "bpm-econ";
export const FORECASTS_S3_FOLDERS = "bppm/econ_bigsix_forecasting/data";

export enum PageTab {
  SAVED_FORECASTS = "saved-forecasts",
  FORECAST_RESULTS = "forecast-results",
  BUDGET_BUILDER = "budget-builder",
}

export type GraphViewOptions = "showGraph" | "showTable";

export type BudgetViewOptions = "showPercentage" | "showDollars";

export const OUTCOME_VARIABLE_LABELS: Record<OutcomeVariableType, string> = {
  Volume: "KPI Volume",
  Revenue: "Revenue KPI",
};

export enum ForecastKpiViewOption {
  TOTAL = "Total",
  EFFICIENCY = "Efficiency",
}

export const ForecastChartViewLabels: Record<
  OutcomeVariableType,
  Record<ForecastKpiViewOption, string>
> = {
  Volume: {
    [ForecastKpiViewOption.TOTAL]: OUTCOME_VARIABLE_LABELS.Volume,
    [ForecastKpiViewOption.EFFICIENCY]: "CPA",
  },
  Revenue: {
    [ForecastKpiViewOption.TOTAL]: OUTCOME_VARIABLE_LABELS.Revenue,
    [ForecastKpiViewOption.EFFICIENCY]: "ROAS",
  },
};

export const KPI_VIEW_SUFFICES: Record<ForecastKpiViewOption, string> = {
  [ForecastKpiViewOption.TOTAL]: "kpi",
  [ForecastKpiViewOption.EFFICIENCY]: "kpiEfficiency",
};

export const KPI_CI_VIEW_SUFFICES: Record<ForecastKpiViewOption, string> = {
  [ForecastKpiViewOption.TOTAL]: "confidenceInterval",
  [ForecastKpiViewOption.EFFICIENCY]: "efficiencyConfidenceInterval",
};

export const NAVS: { label: string; key: PageTab }[] = [
  { label: "Saved Forecasts", key: PageTab.SAVED_FORECASTS },
  { label: "Forecast Results", key: PageTab.FORECAST_RESULTS },
  { label: "Budget Builder", key: PageTab.BUDGET_BUILDER },
];

type KpiString = string;
export type KpiOutcomeString = `${KpiString}, ${OutcomeVariableType}`;

// Unused columns in the _fold_data.csv S3 as of 8/1/25:
// MAE, addmult, channel, efficiency_metric, lags, model
export interface FoldDataRow {
  ds: BpmDateString;
  y: number;
  yhat: number;
  yhat_lower: number;
  yhat_upper: number;
  efficiency_estimate: number;
  efficiency_estimate_lower: number;
  efficiency_estimate_upper: number;
  cpm: number;
  spend: number;
  cutoff: BpmDateString;
  kpi: string;
  outcome: "revenue" | "volume";
  impressions: number;
}

// Unused columns in the _allbudgets_bestmodel.csv S3 as of 8/1/25:
// client, channel, mae, MAE, mape, mase, mse, model, addmult, spend_coef, lags, company, budgets, impressions, efficiency_metric
export interface BestModelRow {
  kpi: string;
  outcome: "revenue" | "volume";
  budget: number;
  ds: BpmDateString;
  yhat: number;
  yhat_lower: number;
  yhat_upper: number;
  efficiency_estimate: number;
  efficiency_estimate_lower: number;
  efficiency_estimate_upper: number;
  spend: number;
  cpm: number;
}

export interface TrainTestData {
  date: BpmDateString;
  timestamp: number;
  actual_kpi: number;
  predicted_kpi: number;
  predicted_kpi_ci: [number, number];
  predicted_efficiency: number;
  predicted_efficiency_ci: [number, number];
  train_test: "train" | "test";
  spend: number;
  cpm: number;
}

// Unused columns in the _model_summary.csv S3 as of 8/1/25:
// addmult, lags, MASE, fc_start, fc_end, total_spend
export interface ModelSummaryData {
  kpi: string;
  outcome: "revenue" | "volume";
  hist_start: BpmDateString;
  hist_end: BpmDateString;
  model: string;
  avg_spend: number;
  last_fold_mape: number;
  last_fold_mae: number;
  // Technically these are medians and not averages
  avg_MAPE: number;
  avg_MAE: number;
  test: number;
  train: number;
  total_estimate: number;
  total_estimate_lower: number;
  total_estimate_upper: number;
  // Technically these "CPA"s can also be ROAS values, it's just the efficiency metric
  total_cpa: number;
  total_cpa_upper: number;
  total_cpa_lower: number;
}

export interface ComparisonBudgetOption {
  percentValue: number;
  dollarValue: number;
  prettyName: string;
}

type RangeForecastValue = "confidenceInterval" | "efficiencyConfidenceInterval";
type NumericForecastValues = "kpi" | "kpiEfficiency" | "spend" | "cpm";
export type ForecastData<BudgetName extends string> = {
  date: BpmDateString;
  timestamp: number; // Milliseconds since epoch, used for charting on the x-axis
} & {
  [K in `${BudgetName}-${NumericForecastValues}`]: number;
} &
  {
    [K in `${BudgetName}-${RangeForecastValue}`]?: [number, number];
  };

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const EXAMPLE_FORECAST_DATA: ForecastData<"75000" | "100000"> = {
  date: "2025-06-01",
  timestamp: 1748736000000,
  "75000-kpi": 1,
  "75000-confidenceInterval": [1, 2],
  "75000-kpiEfficiency": 1,
  "75000-efficiencyConfidenceInterval": [1, 2],
  "75000-spend": 0,
  "75000-cpm": 0,
  "100000-kpi": 2,
  "100000-confidenceInterval": [3, 4],
  "100000-kpiEfficiency": 2,
  "100000-efficiencyConfidenceInterval": [1, 2],
  "100000-spend": 2,
  "100000-cpm": 0,
};

export type FullForecastSelection = Omit<ForecastGroup, "forecasts"> &
  Omit<Forecast, "kpis"> & {
    kpiName: string;
    outcomeVar: OutcomeVariableType;
  };

export enum SelectableForecastProperty {
  CHANNEL_PLATFORM = "channel-platform",
  OUTCOME_VARIABLE = "outcome-variable",
  KPI = "kpi",
  BUDGET = "budget",
  DATE_RANGE = "date-range",
}

export interface ForecastPropertyTypesMap {
  [SelectableForecastProperty.CHANNEL_PLATFORM]: string;
  [SelectableForecastProperty.OUTCOME_VARIABLE]: OutcomeVariableType;
  [SelectableForecastProperty.KPI]: string;
  [SelectableForecastProperty.BUDGET]: number;
  [SelectableForecastProperty.DATE_RANGE]: string;
}
export type ForecastPropertyValueType<
  P extends SelectableForecastProperty
> = ForecastPropertyTypesMap[P];

export type MatchableForecastData = {
  [K in keyof ForecastPropertyTypesMap]: ForecastPropertyTypesMap[K];
} & { forecastId: string };

export type MatchableForecastOptions = {
  [K in keyof ForecastPropertyTypesMap]: ForecastPropertyTypesMap[K][];
};

export const DEFAULT_COMPARISON_BUDGET_PERCENTAGES: number[] = [-75, -50, -25, 25, 50, 75, 100];

export const HISTORICAL_KEY = "historical";
export const NA_VALUE = "N/A";

export const formatRawBudget = (val: number): string => `$${abbreviateNumber(val)}`;
export const formatPercentBudget = (val: number): string => `${abbreviateNumber(val * 100)}%`;
export const formatSpend = formatMoneyAsInt;
export const formatAxisSpend = (val: number): string => `$${abbreviateNumber(val)}`;
export const formatCpm = (val: number): string => formatMoney(val, 2);
export const formatAxisCpm = (val: number): string => formatMoney(val, 0);
